import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Save, Settings as SettingsIcon, Building, CreditCard, Globe, AlertTriangle } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site_name: '',
    site_description: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    bank_name: '',
    bank_iban: '',
    bank_account_holder: '',
    commission_rate: 10,
    maintenance_mode: false,
    maintenance_message: '',
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setForm({
        site_name: data.site_name || '',
        site_description: data.site_description || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        contact_address: data.contact_address || '',
        bank_name: data.bank_name || '',
        bank_iban: data.bank_iban || '',
        bank_account_holder: data.bank_account_holder || '',
        commission_rate: data.commission_rate ?? 10,
        maintenance_mode: !!data.maintenance_mode,
        maintenance_message: data.maintenance_message || '',
      });
    } catch (e) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        ...form,
        commission_rate: Number(form.commission_rate),
      }, { withCredentials: true });
      toast.success('Ayarlar kaydedildi');
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-emerald-600" />
            Sistem Ayarları
          </h1>
          <p className="text-sm text-slate-600 mt-1">Siteyi, ödemeleri ve iletişim bilgilerini buradan yönetin</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      {/* Site Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5 text-emerald-600" />
            Site Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Site Adı</Label>
            <Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} data-testid="settings-site-name" />
          </div>
          <div>
            <Label>Site Açıklaması (SEO)</Label>
            <Textarea value={form.site_description} onChange={(e) => setForm({ ...form, site_description: e.target.value })} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building className="w-5 h-5 text-emerald-600" />
            İletişim Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>İletişim E-postası</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <Label>İletişim Telefonu</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+90 555 123 4567" />
            </div>
          </div>
          <div>
            <Label>Adres</Label>
            <Textarea value={form.contact_address} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} rows={2} />
          </div>
        </CardContent>
      </Card>

      {/* Banking / Bank Transfer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Havale Bilgileri
          </CardTitle>
          <p className="text-xs text-slate-500">Bu bilgiler havale ile ödeme yapan müşterilere gösterilir</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Banka Adı</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Garanti BBVA" />
            </div>
            <div>
              <Label>Hesap Sahibi</Label>
              <Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} placeholder="TesisKirala A.Ş." />
            </div>
          </div>
          <div>
            <Label>IBAN</Label>
            <Input value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value })} placeholder="TR00 0000 0000 0000 0000 0000 00" className="font-mono" data-testid="settings-bank-iban" />
          </div>
          <div>
            <Label>Komisyon Oranı (%)</Label>
            <Input type="number" step="0.5" min="0" max="100" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="max-w-xs" />
            <p className="text-xs text-slate-500 mt-1">Rezervasyon başına platform komisyonu</p>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <Card className={form.maintenance_mode ? 'border-amber-300 bg-amber-50/50' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Bakım Modu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div>
              <div className="font-medium text-slate-900">Bakım Modu</div>
              <p className="text-xs text-slate-500">Aktifken kullanıcılara bakım mesajı gösterilir</p>
            </div>
            <Switch checked={form.maintenance_mode} onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })} data-testid="settings-maintenance-toggle" />
          </div>
          {form.maintenance_mode && (
            <div>
              <Label>Bakım Mesajı</Label>
              <Textarea value={form.maintenance_message} onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })} rows={2} />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
        </Button>
      </div>
    </div>
  );
}
